import type {
  ApiErrorBody,
  AuthSession,
  BackupsView,
  BudgetPolicy,
  CallGrant,
  Conversation,
  Device,
  DeletionPreview,
  Initiative,
  InitiativePolicy,
  InitiativeSettings,
  MemoryItem,
  MemorySearchHit,
  MemoryRecord,
  Message,
  Operation,
  OwnerStatus,
  PageResult,
  Persona,
  PersonaContextPlan,
  PersonaProfileView,
  PersonaVersionSummary,
  PersonaTimelineEntry,
  PriceEntry,
  ProviderConfig,
  ProviderConfigVersion,
  ProviderCapability,
  ProviderProbeResult,
  PushSubscriptionInfo,
  QueuedOperation,
  RecoveryBundleSummary,
  CanonEntry,
  LifeEvent,
  MediaAsset,
  Recording,
  TrashItem,
  UsageSummary
} from './types';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly operationId?: string;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || 'تعذر إكمال الطلب.');
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code || 'UNKNOWN_ERROR';
    if (body.operationId) this.operationId = body.operationId;
  }
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const prefix = `${encodeURIComponent(name)}=`;
  return document.cookie.split('; ').find((item) => item.startsWith(prefix))?.slice(prefix.length);
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return typeof value === 'object' && value !== null;
}

export class ApiClient {
  constructor(private readonly baseUrl = '/api/v1') {}

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    const csrf = readCookie('__Host-rami_csrf');
    if (csrf && init.method && init.method !== 'GET' && init.method !== 'HEAD') {
      headers.set('x-csrf-token', decodeURIComponent(csrf));
    }
    if (init.body && !(init.body instanceof FormData) && !headers.has('content-type')) {
      headers.set('content-type', 'application/json');
    }
    headers.set('accept', 'application/json');

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers,
      credentials: 'include'
    });
    const contentType = response.headers.get('content-type') || '';
    const body: unknown = contentType.includes('application/json') ? await response.json() : undefined;
    if (!response.ok) {
      throw new ApiError(response.status, isApiErrorBody(body) ? body : {});
    }
    return body as T;
  }

  login(accessCode: string, rememberMe: boolean): Promise<{ session: AuthSession }> {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ accessCode, rememberMe })
    });
  }

  logout(): Promise<void> {
    return this.request('/auth/logout', { method: 'POST' });
  }

  sessions(): Promise<PageResult<AuthSession>> {
    return this.request('/auth/sessions');
  }

  ownerStatus(): Promise<OwnerStatus> {
    return this.request('/owner/status');
  }

  revokeSession(id: string): Promise<void> {
    return this.request(`/auth/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  conversations(): Promise<PageResult<Conversation>> {
    return this.request('/conversations');
  }

  createConversation(): Promise<Conversation> {
    return this.request('/conversations', { method: 'POST', body: JSON.stringify({}) });
  }

  messages(conversationId: string, before?: string): Promise<PageResult<Message>> {
    const query = new URLSearchParams({ limit: '50' });
    if (before) query.set('before', before);
    return this.request(`/conversations/${encodeURIComponent(conversationId)}/messages?${query}`);
  }

  sendMessage(
    conversationId: string,
    clientMessageId: string,
    operationId: string,
    text: string,
    requestImage = false
  ): Promise<{ message: Message; duplicate: boolean }> {
    return this.request(`/conversations/${encodeURIComponent(conversationId)}/messages`, {
      method: 'POST',
      headers: { 'idempotency-key': clientMessageId },
      body: JSON.stringify({ clientMessageId, operationId, text, ...(requestImage ? { requestImage: true } : {}) })
    });
  }

  cancelMessage(id: string): Promise<void> {
    return this.request(`/messages/${encodeURIComponent(id)}/cancel`, { method: 'POST' });
  }

  startCall(input: { sdpOffer: string; recordingEnabled: boolean; deviceId?: string }): Promise<CallGrant> {
    return this.request('/voice/calls', { method: 'POST', body: JSON.stringify(input) });
  }

  connectCall(callId: string, input: { sdpOffer: string; recordingEnabled: boolean; deviceId?: string }): Promise<CallGrant> {
    return this.request(`/voice/calls/${encodeURIComponent(callId)}/connect`, { method: 'POST', body: JSON.stringify(input) });
  }

  endCall(callId: string, generation: number): Promise<void> {
    return this.request(`/voice/calls/${encodeURIComponent(callId)}/end`, {
      method: 'POST',
      body: JSON.stringify({ generation })
    });
  }

  acceptInvite(inviteId: string, deviceId?: string): Promise<{ callId: string }> {
    return this.request(`/voice/invites/${encodeURIComponent(inviteId)}/accept`, {
      method: 'POST',
      body: JSON.stringify({ deviceId })
    });
  }

  rejectInvite(inviteId: string): Promise<void> {
    return this.request(`/voice/invites/${encodeURIComponent(inviteId)}/reject`, { method: 'POST' });
  }

  devices(): Promise<PageResult<Device>> { return this.request('/devices'); }
  setPreferredDevice(id: string, revision?: number): Promise<Device> {
    return this.request(`/devices/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify({ preferred: true, revision })
    });
  }
  revokeDevice(id: string): Promise<void> { return this.request(`/devices/${encodeURIComponent(id)}`, { method: 'DELETE' }); }

  providers(): Promise<PageResult<ProviderConfig>> { return this.request('/owner/providers'); }
  providerCapabilities(): Promise<PageResult<ProviderCapability>> { return this.request('/owner/providers'); }
  providerVersions(provider: string, capability: string): Promise<{ items: ProviderConfigVersion[] }> {
    return this.request(`/owner/providers/${encodeURIComponent(provider)}/versions?capability=${encodeURIComponent(capability)}`);
  }
  createProviderDraft(provider: string, input: {
    capability: string;
    model?: string;
    retentionPolicy?: 'provider_default' | 'no_store';
    store?: boolean;
    baseUrl?: string | null;
    credential?: string;
  }): Promise<ProviderConfigVersion & { credentialFingerprint: string | null; canTest: boolean }> {
    return this.request(`/owner/providers/${encodeURIComponent(provider)}/drafts`, {
      method: 'POST',
      body: JSON.stringify(input)
    });
  }
  validateProviderVersion(provider: string, capability: string, version: number): Promise<ProviderConfigVersion> {
    return this.request(`/owner/providers/${encodeURIComponent(provider)}/validate`, {
      method: 'POST',
      body: JSON.stringify({ capability, version })
    });
  }
  testProviderCapability(provider: string, capability: string): Promise<ProviderProbeResult> {
    return this.request(`/owner/providers/${encodeURIComponent(provider)}/test`, {
      method: 'POST',
      body: JSON.stringify({ capability })
    });
  }
  activateProviderVersion(
    provider: string,
    input: { capability: string; version: number; expectedRevision?: number }
  ): Promise<ProviderConfigVersion & { note: string; liveVerified: boolean }> {
    return this.request(`/owner/providers/${encodeURIComponent(provider)}/activate`, {
      method: 'POST',
      body: JSON.stringify(input)
    });
  }
  revokeProviderCredential(provider: string, capability: string): Promise<{ provider: string; status: string }> {
    return this.request(`/owner/providers/${encodeURIComponent(provider)}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ capability })
    });
  }

  usage(): Promise<UsageSummary> { return this.request('/owner/usage'); }
  budget(): Promise<{ items: BudgetPolicy[]; prices: PriceEntry[] }> { return this.request('/owner/budget'); }
  saveBudget(input: {
    scope: 'development' | 'operations';
    dailyLimitMicrounits: number | null;
    monthlyLimitMicrounits: number | null;
    timezone?: string;
  }): Promise<BudgetPolicy> {
    return this.request('/owner/budget', { method: 'POST', body: JSON.stringify(input) });
  }
  activateBudget(scope: 'development' | 'operations', expectedRevision?: number): Promise<BudgetPolicy> {
    return this.request(`/owner/budget/${scope}/activate`, {
      method: 'POST',
      body: JSON.stringify(expectedRevision === undefined ? {} : { expectedRevision })
    });
  }
  operations(): Promise<PageResult<Operation>> { return this.request('/owner/operations?limit=50'); }
  backups(): Promise<BackupsView> { return this.request('/owner/backups?limit=20'); }
  startBackup(idempotencyKey: string): Promise<QueuedOperation> {
    return this.request('/owner/backups/start', { method: 'POST', body: JSON.stringify({ idempotencyKey }) });
  }
  testRestore(backupId: string, idempotencyKey?: string): Promise<QueuedOperation> {
    return this.request(`/owner/backups/${encodeURIComponent(backupId)}/verify-restore`, {
      method: 'POST',
      body: JSON.stringify(idempotencyKey ? { idempotencyKey } : {})
    });
  }
  recoveryBundles(): Promise<{ items: RecoveryBundleSummary[]; localCommandOnly: boolean }> {
    return this.request('/owner/recovery-bundles');
  }
  sensitiveReauth(accessCode: string): Promise<void> {
    return this.request('/auth/sensitive-reauth', { method: 'POST', body: JSON.stringify({ accessCode }) });
  }
  requestRecoveryBundle(): Promise<{ capability: string; status: string; command: string; message: string }> {
    return this.request('/owner/recovery-bundles', { method: 'POST' });
  }

  personas(): Promise<PageResult<Persona>> { return this.request('/personas'); }
  personaProfile(id: string): Promise<PersonaProfileView> { return this.request(`/personas/${encodeURIComponent(id)}/profile`); }
  personaVersions(id: string): Promise<PageResult<PersonaVersionSummary>> { return this.request(`/personas/${encodeURIComponent(id)}/versions`); }
  personaTimeline(id: string): Promise<PageResult<PersonaTimelineEntry>> { return this.request(`/personas/${encodeURIComponent(id)}/timeline`); }
  personaItem(id: string, input: { kind: 'opinion' | 'interest'; topic: string; stance: string; confidence?: number }): Promise<{ id: string; status: string; created: boolean }> {
    return this.request(`/personas/${encodeURIComponent(id)}/items`, {
      method: 'POST',
      body: JSON.stringify({ ...input, reason: 'إضافة من لوحة المالك', operationId: crypto.randomUUID() })
    });
  }
  personaItemDecision(id: string, itemId: string, action: 'approve' | 'reject' | 'invalidate' | 'supersede'): Promise<{ id: string; status: string }> {
    return this.request(`/personas/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}/decision`, {
      method: 'POST',
      body: JSON.stringify({ action, reason: 'قرار من لوحة المالك', operationId: crypto.randomUUID() })
    });
  }
  personaContextPreview(id: string, input: { channel?: 'text' | 'voice'; query?: string; includeSelfExperience?: boolean } = {}): Promise<PersonaContextPlan> {
    return this.request(`/personas/${encodeURIComponent(id)}/context-preview`, { method: 'POST', body: JSON.stringify(input) });
  }
  ensurePersonaIdentity(id: string): Promise<{ applied: boolean; version: number; filledFields: string[] }> {
    return this.request(`/personas/${encodeURIComponent(id)}/ensure-identity`, {
      method: 'POST', body: JSON.stringify({ operationId: crypto.randomUUID() })
    });
  }
  memoryItems(query = '', truthClass = '', includeBlocked = false): Promise<PageResult<MemoryRecord>> {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (truthClass) params.set('truthClass', truthClass);
    if (includeBlocked) params.set('includeBlocked', 'true');
    const suffix = params.toString();
    return this.request('/memories/items' + (suffix ? '?' + suffix : ''));
  }
  memoryImpact(id: string): Promise<{ sources: number; embeddings: number; relatedOpenLoops: number; sharedMemorySources: number }> {
    return this.request(`/memories/${encodeURIComponent(id)}/impact`);
  }
  forgetMemory(id: string): Promise<{ request: DeletionPreview }> {
    return this.request(`/memories/${encodeURIComponent(id)}/forget`, {
      method: 'POST', body: JSON.stringify({ reason: 'نسيان من لوحة المالك', operationId: crypto.randomUUID() })
    });
  }
  setMemoryBlocked(id: string, blocked: boolean): Promise<{ memoryId: string; blocked: boolean }> {
    return this.request(`/memories/${encodeURIComponent(id)}/usage-blocked`, {
      method: 'POST', body: JSON.stringify({ blocked, reason: 'قرار من لوحة المالك', operationId: crypto.randomUUID() })
    });
  }

  // ---- Stage 10.5 additions ----
  personaPreferences(conversationId?: string): Promise<{ replyMode: 'text' | 'voice_note'; outgoingImageMode: 'generate' | 'internet' | 'off'; ambientEnabled: boolean; ambientLevel: number; revision: number }> {
    return this.request('/persona-preferences' + (conversationId ? '?conversationId=' + encodeURIComponent(conversationId) : ''));
  }
  savePersonaPreferences(input: { conversationId?: string; replyMode?: string; outgoingImageMode?: string; ambientEnabled?: boolean; ambientLevel?: number; expectedRevision?: number }): Promise<{ replyMode: 'text' | 'voice_note'; outgoingImageMode: 'generate' | 'internet' | 'off'; ambientEnabled: boolean; ambientLevel: number; revision: number }> {
    return this.request('/persona-preferences', { method: 'PUT', body: JSON.stringify(input) });
  }
  canonEntries(personaId: string): Promise<PageResult<CanonEntry>> { return this.request('/personas/' + encodeURIComponent(personaId) + '/canon'); }
  submitCanon(personaId: string, input: { title: string; body: string; occurredAt?: string; location?: string; requireOwnerApproval?: boolean; sourceConversationId?: string; tense?: 'completed' | 'plan' }): Promise<{ status: string; conflicts: string[]; id: string | null }> {
    return this.request('/personas/' + encodeURIComponent(personaId) + '/canon', {
      method: 'POST', body: JSON.stringify({ ...input, operationId: crypto.randomUUID() })
    });
  }
  decideCanon(personaId: string, entryId: string, decision: 'approve' | 'reject'): Promise<{ id: string; status: string }> {
    return this.request('/personas/' + encodeURIComponent(personaId) + '/canon/' + encodeURIComponent(entryId) + '/decision', {
      method: 'POST', body: JSON.stringify({ decision, operationId: crypto.randomUUID() })
    });
  }
  lifeEvents(personaId: string): Promise<PageResult<LifeEvent>> { return this.request('/personas/' + encodeURIComponent(personaId) + '/life-events'); }
  recordLifeEvent(personaId: string, input: { kind: string; summary: string; location?: string; startsAt?: string; endsAt?: string }): Promise<{ id: string; state: string; policy: string }> {
    return this.request('/personas/' + encodeURIComponent(personaId) + '/life-events', {
      method: 'POST', body: JSON.stringify({ ...input, operationId: crypto.randomUUID() })
    });
  }
  decideLifeEvent(personaId: string, eventId: string, decision: 'approve' | 'reject' | 'complete' | 'cancel'): Promise<{ id: string; state: string }> {
    return this.request('/personas/' + encodeURIComponent(personaId) + '/life-events/' + encodeURIComponent(eventId) + '/decision', {
      method: 'POST', body: JSON.stringify({ decision, operationId: crypto.randomUUID() })
    });
  }
  lifeCatchUp(personaId: string): Promise<{ transitions: number; needsProvider: boolean }> {
    return this.request('/personas/' + encodeURIComponent(personaId) + '/life-events/catch-up', { method: 'POST' });
  }
  mediaAssets(kind?: 'image' | 'audio', includeDeleted = false): Promise<PageResult<MediaAsset>> {
    const query = new URLSearchParams();
    if (kind) query.set('kind', kind);
    if (includeDeleted) query.set('includeDeleted', 'true');
    return this.request('/media-assets' + (query.size ? '?' + query.toString() : ''));
  }
  uploadMedia(input: {
    kind: 'image' | 'audio';
    direction: 'incoming' | 'outgoing';
    origin: 'upload' | 'voice_note' | 'generated' | 'internet' | 'ambient';
    mimeType: string;
    dataBase64: string;
    durationMs?: number;
    transcript?: string;
    conversationId?: string;
  }): Promise<{ id: string; byteSize: number; checksum: string; mimeType: string; kind: string }> {
    return this.request('/media-assets', { method: 'POST', body: JSON.stringify(input) });
  }
  trashMediaAsset(assetId: string): Promise<{ id: string; status: string }> {
    return this.request('/media-assets/' + encodeURIComponent(assetId) + '/trash', { method: 'POST', body: JSON.stringify({ reason: 'نقل إلى السلة' }) });
  }
  restoreMediaAsset(assetId: string): Promise<{ id: string; status: string }> {
    return this.request('/media-assets/' + encodeURIComponent(assetId) + '/restore', { method: 'POST', body: JSON.stringify({}) });
  }

  sendConversationMedia(input: {
    conversationId: string; clientMessageId: string; operationId: string; kind: 'image' | 'audio';
    origin: 'upload' | 'voice_note'; mimeType: string; dataBase64: string; caption?: string; durationMs?: number;
  }, onProgress: (percent: number) => void): Promise<{ assetId: string; messageId: string; duplicate: boolean }> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.baseUrl}/conversations/${encodeURIComponent(input.conversationId)}/media`);
      xhr.withCredentials = true;
      xhr.responseType = 'json';
      xhr.setRequestHeader('content-type', 'application/json');
      xhr.setRequestHeader('accept', 'application/json');
      const csrf = readCookie('__Host-rami_csrf');
      if (csrf) xhr.setRequestHeader('x-csrf-token', decodeURIComponent(csrf));
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response as { assetId: string; messageId: string; duplicate: boolean });
        else reject(new ApiError(xhr.status, isApiErrorBody(xhr.response) ? xhr.response : {}));
      };
      xhr.onerror = () => reject(new ApiError(0, { code: 'NETWORK_ERROR', message: 'تعذر رفع الوسيط. تحقق من الاتصال وحاول مجددًا.' }));
      xhr.send(JSON.stringify(input));
    });
  }
  purgeMediaAsset(assetId: string): Promise<{ id: string; purged: boolean }> {
    return this.request('/media-assets/' + encodeURIComponent(assetId) + '/purge', { method: 'POST', body: JSON.stringify({ reason: 'حذف نهائي' }) });
  }
  mediaUrl(assetId: string): string { return this.baseUrl + '/media-assets/' + encodeURIComponent(assetId); }
  deletionRequests(): Promise<PageResult<DeletionPreview>> { return this.request('/deletion-requests?limit=50'); }
  deleteMessage(id: string): Promise<DeletionPreview> {
    return this.request(`/messages/${encodeURIComponent(id)}/deletion`, {
      method: 'POST', body: JSON.stringify({ reason: 'من لوحة المالك', mode: 'permanent', operationId: crypto.randomUUID() })
    });
  }
  deleteConversation(id: string): Promise<DeletionPreview> {
    return this.request(`/conversations/${encodeURIComponent(id)}/deletion`, {
      method: 'POST', body: JSON.stringify({ reason: 'من لوحة المالك', mode: 'permanent', operationId: crypto.randomUUID() })
    });
  }
  purgeConversation(id: string): Promise<{ removed: boolean; messagesRemoved: number; callsRemoved: number; blockedByLiveCall: boolean }> {
    return this.request('/conversations/' + encodeURIComponent(id) + '/purge', { method: 'POST' });
  }
  moveMessageToTrash(id: string): Promise<DeletionPreview> {
    return this.request(`/messages/${encodeURIComponent(id)}/deletion`, {
      method: 'POST', body: JSON.stringify({ reason: 'نقل إلى السلة', mode: 'trash', operationId: crypto.randomUUID() })
    });
  }
  moveConversationToTrash(id: string): Promise<DeletionPreview> {
    return this.request(`/conversations/${encodeURIComponent(id)}/deletion`, {
      method: 'POST', body: JSON.stringify({ reason: 'نقل إلى السلة', mode: 'trash', operationId: crypto.randomUUID() })
    });
  }
  deleteRecordingScoped(id: string, scope: 'audio' | 'audio_derivatives' | 'all'): Promise<DeletionPreview> {
    return this.request(`/recordings/${encodeURIComponent(id)}/deletion`, {
      method: 'POST', body: JSON.stringify({ scope, reason: 'حذف تسجيل من لوحة المالك', operationId: crypto.randomUUID() })
    });
  }
  personaDraft(id: string, fields: Record<string, unknown>, reason: string, expectedPinnedVersionId?: string) {
    return this.request<{ versionId: string; version: number; rejectedFields: string[] }>(`/personas/${encodeURIComponent(id)}/drafts`, {
      method: 'POST',
      body: JSON.stringify({ fields, reason, operationId: crypto.randomUUID(), ...(expectedPinnedVersionId ? { expectedPinnedVersionId } : {}) })
    });
  }
  personaDiff(id: string, versionId: string) {
    return this.request<{ fromVersion: number; toVersion: number; changes: Array<{ field: string; from: unknown; to: unknown }> }>(
      `/personas/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/diff`
    );
  }
  personaActivate(id: string, versionId: string, reason: string, expectedPinnedVersionId?: string) {
    return this.request<{ pinnedVersionId: string; changed: boolean }>(
      `/personas/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/activate`,
      { method: 'POST', body: JSON.stringify({ reason, operationId: crypto.randomUUID(), ...(expectedPinnedVersionId ? { expectedPinnedVersionId } : {}) }) }
    );
  }
  personaChanges(id: string) {
    return this.request<{ items: Array<{ id: string; kind: string; scope: string; reason: string; createdAt: string }> }>(
      `/personas/${encodeURIComponent(id)}/changes`
    );
  }
  personaRollbackTrait(id: string, traitKey: string, targetValue: number, reason: string) {
    return this.request<{ applied: boolean; code: string; fromValue?: number; toValue?: number }>(
      `/personas/${encodeURIComponent(id)}/rollback`,
      { method: 'POST', body: JSON.stringify({ scope: 'trait', traitKey, targetValue, reason, operationId: crypto.randomUUID() }) }
    );
  }
  updatePersona(id: string, input: Record<string, unknown>): Promise<Persona> {
    return this.request(`/personas/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) });
  }
  revertPersona(id: string, targetRevision: number): Promise<Operation> {
    return this.request(`/personas/${encodeURIComponent(id)}/revert`, {
      method: 'POST', body: JSON.stringify({ targetRevision })
    });
  }

  memories(search: string, status: string): Promise<PageResult<MemoryItem>> {
    const query = new URLSearchParams({ limit: '50' });
    if (search) query.set('q', search);
    if (status) query.set('status', status);
    return this.request(`/memories?${query}`);
  }
  decideMemory(id: string, decision: 'approve' | 'reject', revision?: number): Promise<MemoryItem> {
    return this.request(`/memories/${encodeURIComponent(id)}/decision`, {
      method: 'POST', body: JSON.stringify({ decision, revision })
    });
  }
  searchMemories(query: string, limit = 10): Promise<{ items: MemorySearchHit[]; mode: string; degraded: boolean; vectorStatus: string }> {
    const search = new URLSearchParams({ q: query, limit: String(limit) });
    return this.request(`/memories/search?${search}`);
  }
  correctMemory(id: string, text: string, revision: number, reason: string): Promise<MemoryItem> {
    return this.request(`/memories/${encodeURIComponent(id)}/correct`, {
      method: 'POST', body: JSON.stringify({ text, revision, reason, operationId: crypto.randomUUID() })
    });
  }
  /** Default user-facing deletion: reversible move to trash. */
  moveToTrash(scope: DeletionPreview['scope'], targetIds: string[], scopeOptions: Record<string, unknown> = {}): Promise<DeletionPreview> {
    return this.request('/deletion-requests', {
      method: 'POST',
      body: JSON.stringify({ scope, targetIds, mode: 'trash', scopeOptions, operationId: crypto.randomUUID() })
    });
  }
  /** Irreversible deletion preview; nothing is removed until confirmDeletion is called. */
  previewPermanentDeletion(scope: DeletionPreview['scope'], targetIds: string[], scopeOptions: Record<string, unknown> = {}): Promise<DeletionPreview> {
    return this.request('/deletion-requests', {
      method: 'POST',
      body: JSON.stringify({ scope, targetIds, mode: 'permanent', scopeOptions, operationId: crypto.randomUUID() })
    });
  }
  requestDeletion(scope: DeletionPreview['scope'], targetIds: string[]): Promise<DeletionPreview> {
    return this.moveToTrash(scope, targetIds);
  }
  trash(): Promise<PageResult<TrashItem>> { return this.request('/trash?limit=100'); }
  restoreFromTrash(requestId: string): Promise<{ request: DeletionPreview; restored: number; keptHidden: string[] }> {
    return this.request(`/deletion-requests/${encodeURIComponent(requestId)}/restore`, { method: 'POST' });
  }
  permanentFromTrash(requestId: string): Promise<DeletionPreview> {
    return this.request(`/deletion-requests/${encodeURIComponent(requestId)}/permanent`, {
      method: 'POST', body: JSON.stringify({ operationId: crypto.randomUUID() })
    });
  }
  confirmDeletion(requestId: string): Promise<DeletionPreview> {
    return this.request(`/deletion-requests/${encodeURIComponent(requestId)}/confirm`, { method: 'POST' });
  }
  deletionRequest(requestId: string): Promise<DeletionPreview> {
    return this.request(`/deletion-requests/${encodeURIComponent(requestId)}`);
  }

  recordings(): Promise<PageResult<Recording>> { return this.request('/recordings?limit=50'); }
  async uploadRecordingChunk(input: {
    recordingId: string;
    channel: 'owner' | 'rami';
    sequence: number;
    ciphertext: ArrayBuffer;
    iv: string;
    checksum: string;
    startMs: number;
    endMs: number;
  }): Promise<{ acknowledged: boolean }> {
    const query = new URLSearchParams({ channel: input.channel, sequence: String(input.sequence) });
    return this.request(`/recordings/${encodeURIComponent(input.recordingId)}/chunks?${query}`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/octet-stream',
        'x-recording-iv': input.iv,
        'x-recording-checksum': input.checksum,
        'x-recording-start-ms': String(input.startMs),
        'x-recording-end-ms': String(input.endMs)
      },
      body: input.ciphertext
    });
  }
  completeRecording(id: string): Promise<Recording> {
    return this.request(`/recordings/${encodeURIComponent(id)}/complete`, { method: 'POST' });
  }
  retainRecording(id: string): Promise<Recording> {
    return this.request(`/recordings/${encodeURIComponent(id)}/retain`, { method: 'POST' });
  }
  deleteRecording(id: string): Promise<void> { return this.request(`/recordings/${encodeURIComponent(id)}`, { method: 'DELETE' }); }

  pushPublicKey(): Promise<{ publicKey: string }> { return this.request('/push-subscriptions/public-key'); }
  createPushSubscription(subscription: PushSubscriptionJSON): Promise<Device> {
    return this.request('/push-subscriptions', { method: 'POST', body: JSON.stringify(subscription) });
  }
  removePushSubscription(endpoint: string): Promise<void> {
    return this.request('/push-subscriptions', { method: 'DELETE', body: JSON.stringify({ endpoint }) });
  }
  pushSubscriptions(): Promise<PageResult<PushSubscriptionInfo>> { return this.request('/push-subscriptions'); }

  initiatives(status?: string): Promise<PageResult<Initiative>> {
    const query = status ? '?' + new URLSearchParams({ status }).toString() : '';
    return this.request('/initiatives' + query);
  }
  initiative(id: string): Promise<Initiative> { return this.request('/initiatives/' + encodeURIComponent(id)); }
  requestInitiativeCheck(input: { kind?: 'message' | 'call_invite'; topic?: string; reason?: string }): Promise<QueuedOperation> {
    return this.request('/initiatives/check', { method: 'POST', body: JSON.stringify(input) });
  }
  initiativeSettings(): Promise<InitiativeSettings> { return this.request('/initiative-settings'); }
  updateInitiativeSettings(input: {
    policy?: InitiativePolicy;
    enabled?: boolean;
    timezone?: string;
    quietStart?: string;
    quietEnd?: string;
    expectedRevision: number;
  }): Promise<InitiativeSettings> {
    return this.request('/initiative-settings', { method: 'PUT', body: JSON.stringify(input) });
  }
  setInitiativeTopic(input: { topic: string; enabled: boolean; expectedRevision?: number }): Promise<{ topic: string; enabled: boolean; revision: number }> {
    return this.request('/initiative-topics', { method: 'POST', body: JSON.stringify(input) });
  }
  acceptCallInvite(inviteId: string): Promise<{ callId: string; inviteId: string; expiresAt: string }> {
    return this.request('/voice/invites/' + encodeURIComponent(inviteId) + '/accept', { method: 'POST', body: JSON.stringify({}) });
  }
  callInvite(inviteId: string): Promise<{ inviteId: string; callId: string; state: string; expiresAt: string; initiativeId: string | null }> {
    return this.request('/voice/invites/' + encodeURIComponent(inviteId));
  }
}

export const api = new ApiClient();

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof DOMException && error.name === 'NotAllowedError') return 'لم تُمنح صلاحية الميكروفون أو الإشعارات.';
  if (error instanceof Error && error.message) return error.message;
  return 'تعذر إكمال الطلب. تحقق من الاتصال وحاول مجددًا.';
}
